import axios from 'axios';
import { useEffect, useState } from 'react';
import { apiErrorMessage } from '../apiError';
import { getAPIURL } from '../host';
import { Discovery, Family } from '../models/Discovery';
import { Occurrence } from '../models/Pattern';

export interface Loadable<T> {
  data: T | undefined;
  error: string | undefined;
  isLoading: boolean;
}

const load = async <T>(url: string, params?: object): Promise<T> =>
  (await axios.get<T>(url, { params })).data;

/** The automatically discovered pattern families of a sheet (precomputed by the backend). */
export const useDiscovery = (musicSheetID: string | undefined): Loadable<Discovery> => {
  const [state, setState] = useState<Loadable<Discovery>>({
    data: undefined,
    error: undefined,
    isLoading: false
  });

  useEffect(() => {
    if (!musicSheetID) return;
    let isCurrent = true;
    setState({ data: undefined, error: undefined, isLoading: true });
    load<Discovery>(`${getAPIURL()}/discovery/${musicSheetID}`)
      .then((data) => isCurrent && setState({ data, error: undefined, isLoading: false }))
      .catch(
        (error) =>
          isCurrent &&
          setState({ data: undefined, error: apiErrorMessage(error), isLoading: false })
      );

    return () => {
      isCurrent = false;
    };
  }, [musicSheetID]);

  return state;
};

/** The family (occurrences, transitions, distributions) of one pattern, e.g. a pattern selected by the analyst. */
export const useFamily = (
  musicSheetID: string | undefined,
  occurrence: Occurrence | undefined
): Loadable<Family> => {
  const [state, setState] = useState<Loadable<Family>>({
    data: undefined,
    error: undefined,
    isLoading: false
  });

  useEffect(() => {
    if (!musicSheetID || !occurrence) {
      setState({ data: undefined, error: undefined, isLoading: false });
      return;
    }
    let isCurrent = true;
    setState({ data: undefined, error: undefined, isLoading: true });
    load<Family>(`${getAPIURL()}/family/${musicSheetID}`, occurrence)
      .then((data) => isCurrent && setState({ data, error: undefined, isLoading: false }))
      .catch(
        (error) =>
          isCurrent &&
          setState({ data: undefined, error: apiErrorMessage(error), isLoading: false })
      );

    return () => {
      isCurrent = false;
    };
  }, [musicSheetID, occurrence?.voiceNumber, occurrence?.firstOffset, occurrence?.lastOffset]);

  return state;
};
